import { z } from 'zod';
import { dimensionSchema } from './dimension.js';
import { matrixDestinataireSchema, matrixPivotSchema } from './matrix-pivot.js';
import { MEMO_PREVIEW_MAX, NOTE_EXCERPT_MAX } from './note.js';
import { resolvedPalierSchema } from './palier.js';

export const matrixStateSchema = z.enum(['brouillon', 'génération_en_cours', 'généré']);
export type MatrixState = z.infer<typeof matrixStateSchema>;

/**
 * Column widths, hidden columns/dimensions, zoom, activation-row collapsed
 * state — shared across all viewers of a matrix, not per-user. Concrete
 * shape as of the 2026-08-06 design doc's session 3 (view-state features:
 * resize/hide-show/zoom/collapse) — previously left as opaque `unknown`
 * since nothing read or wrote it yet.
 */
export const matrixViewStateShapeSchema = z.object({
  /** Palier numbers currently hidden. */
  hiddenColumns: z.array(z.number().int()),
  /** `dimensions.id`s currently hidden. */
  hiddenDimensionIds: z.array(z.string().uuid()),
  /** The whole 6-levier "Activation des leviers" block — one toggle, not per-levier (E15). */
  activationRowCollapsed: z.boolean(),
  /** Keyed by palier number, as a string. Pixel width. */
  columnWidths: z.record(z.string(), z.number().int().positive()),
  /** Keyed by `${rowType}:${rowRef}`. Pixel height. */
  rowHeights: z.record(z.string(), z.number().int().positive()),
  /** Index into 4 discrete text-size steps (E13). Not undo-able — no `resize`/`toggle_visibility` action logs a zoom change. */
  zoomStep: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
  /**
   * When the "your context changed, these cells predate it" banner was last
   * dismissed (2026-08-26 design doc, D26). Riding in `viewState` rather than
   * in its own column is deliberate: `PATCH /matrices/:id/view-state` is
   * already valid in every matrix state, so the dismissal needs no migration
   * and cannot collide with the `brouillon`-only guard on the generic patch.
   *
   * The banner (and the per-cell staleness markers it controls) reappears as
   * soon as `matrix.contextUpdatedAt > dismissedContextChangeAt`, i.e. on the
   * next context change — dismissing hides the *current* change, not all
   * future ones. Dismissal is per matrix, not per user, matching how zoom and
   * the hidden-column set already behave.
   *
   * Optional, not required-nullable: every `viewState` object written before
   * this field existed lacks the key entirely, and those objects still have to
   * validate when the frontend reads one and sends it straight back through
   * the full-object replace.
   */
  dismissedContextChangeAt: z.string().datetime().nullable().optional(),
});
export type MatrixViewStateShape = z.infer<typeof matrixViewStateShapeSchema>;

export const matrixViewStateSchema = matrixViewStateShapeSchema.nullable();
export type MatrixViewState = z.infer<typeof matrixViewStateSchema>;

/** `viewState: null` (every matrix until something writes to it) means "all defaults". */
export const DEFAULT_MATRIX_VIEW_STATE: MatrixViewStateShape = {
  hiddenColumns: [],
  hiddenDimensionIds: [],
  activationRowCollapsed: false,
  columnWidths: {},
  rowHeights: {},
  zoomStep: 1,
  // `dismissedContextChangeAt` is deliberately absent rather than `null`:
  // `resolveViewState` shallow-merges these defaults under whatever is stored,
  // so listing the key here would inject it into every viewState an unrelated
  // resize/hide write touches. Absent already means "never dismissed" (D26).
};

export const matrixSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  sujet: z.string().max(500).nullable(),
  contexte: z.string().max(10_000).nullable(),
  state: matrixStateSchema,
  /**
   * Set when the most recent generation attempt failed and the matrix
   * reverted to `brouillon`; cleared the moment a new attempt starts. See
   * `matrixStateEnum`'s doc comment in `db/schema.ts` for the full lifecycle.
   */
  lastGenerationFailedAt: z.string().datetime().nullable(),
  viewState: matrixViewStateSchema,
  /**
   * The récapitulatif's composed prompt, persisted only if the user
   * manually edited it away from the auto-composed text (see
   * docs/design/matrice-creation-wizard/2026-08-04-matrice-creation-wizard-design.md
   * decisions 13-14). Null means "use the freshly-composed prompt" once
   * generation exists.
   *
   * Since issue #17 it holds the whole resolved prompt (runner text, then
   * the composed context), not the context alone — the runner by itself is
   * about 11k characters, hence a cap well above the old 20k.
   */
  initialPromptOverride: z.string().max(100_000).nullable(),
  /**
   * Undo/redo cursor (2026-08-06 design doc, session 3, E5) — the id of the
   * last-applied `resize`/`toggle_visibility` action, or `null` if nothing's
   * been applied yet / everything's been undone back to the start.
   */
  currentActionId: z.string().uuid().nullable(),
  /**
   * Last time anything feeding the generation prompt changed — sujet,
   * contexte, the context-file set, a digest or the synthesis (2026-08-26
   * design doc, D25). Cells whose current version predates this are stale.
   * Null means nothing has changed since generation, or nothing is generated.
   */
  contextUpdatedAt: z.string().datetime().nullable(),
  /**
   * Last time the matrix's *framing* changed — `sujet` or `contexte`, and
   * nothing else (issue #1).
   *
   * Deliberately narrower than `contextUpdatedAt`, which also moves on a file
   * upload, a digest or a synthesis. A digest is read against the framing, so
   * comparing a digest's age to `contextUpdatedAt` would have every digest
   * invalidate itself the instant it was written. This field is the only
   * timestamp a digest can be measured against.
   */
  framingUpdatedAt: z.string().datetime().nullable(),
  /**
   * When this matrix stopped tracking its company's palier table and started
   * carrying its own copy — stamped once, as the first generation starts
   * (issue #1, `freezeMatrixPaliers`).
   *
   * Exposed to the frontend because it changes what "inherit" *means* in the
   * Cadrage tab's palier editor (issue #3): while null, a palier left alone
   * resolves through the company, so the editor offers "reprendre la valeur de
   * l'entreprise"; once set, the company level is skipped entirely and the
   * fallback is the global default. An editor pointed at the wrong level would
   * mark the wrong paliers as overridden and restore the wrong text on reset.
   */
  paliersFrozenAt: z.string().datetime().nullable(),
  /**
   * The setup field the activation bullets decline (issue #16), and who
   * receives the action the subject describes (issues #21, #25). Both freeze
   * with the paliers: writable only while `paliersFrozenAt` is null.
   */
  pivot: matrixPivotSchema,
  /** Null = « Non défini »: no recipient is sent to the model. */
  destinataire: matrixDestinataireSchema.nullable(),
  /**
   * The free text of `destinataire = 'autre'` (issues #21, #25), stored as
   * typed and trimmed where it is used. Kept when the user switches to a fixed
   * value, and ignored then; blank, it means « Non défini ». Freezes with
   * `destinataire`.
   */
  destinataireAutre: z.string().nullable(),
  /** Computed server-side from the actions log — see E5/E17. Not stored. */
  canUndo: z.boolean(),
  canRedo: z.boolean(),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Matrix = z.infer<typeof matrixSchema>;

/** `POST /matrices` — created empty on wizard mount, before any input. */
export const createMatrixInputSchema = z.object({
  companyId: z.string().uuid(),
  /**
   * The folder to create the matrix in (issue #19): the folder open in the grid.
   * Must belong to the same company, else 422.
   */
  folderId: z.string().uuid().optional(),
});
export type CreateMatrixInput = z.infer<typeof createMatrixInputSchema>;

/**
 * `PATCH /matrices/:id` — autosaved per field, debounced. Content fields only:
 * the route guards these behind `state === 'brouillon'`, so that a stray
 * autosave (a second tab left open on wizard step 2) cannot rewrite the
 * sujet/contexte a `génération_en_cours` job is composing its prompt from.
 *
 * `pivot`, `destinataire` and `destinataireAutre` (issues #16, #21, #25) are further
 * refused 409 once `paliersFrozenAt` is set, a `destinataireAutre` longer than 120
 * characters once trimmed 422 (checked in the route), and a pivot blank on any setup step is refused 422.
 *
 * `viewState` is deliberately NOT here — see `patchMatrixViewStateInputSchema`.
 */
export const patchMatrixInputSchema = matrixSchema
  .pick({
    sujet: true,
    contexte: true,
    initialPromptOverride: true,
    pivot: true,
    destinataire: true,
    destinataireAutre: true,
  })
  .partial();
export type PatchMatrixInput = z.infer<typeof patchMatrixInputSchema>;

/**
 * `PATCH /matrices/:id/view-state` — a full-object replace of `viewState`
 * (like `dimensions`' bulk-replace semantics), used only for the non-undoable
 * `zoomStep` field (E13). Resize and hide/show go through their own
 * action-logged endpoints instead (see `action.ts`).
 *
 * This is a separate route from the generic patch above precisely because it
 * must work in **any** matrix state: the matrix *view* page only ever renders
 * a `généré` matrix, so while zoom rode on the generic patch every zoom click
 * was rejected 409 by that route's `brouillon`-only guard — silently, the
 * frontend mutation having had no error handler. Keeping the two apart means
 * neither rule can leak onto the other's fields, and any field added to the
 * generic patch inherits the content guard by default rather than escaping it.
 */
export const patchMatrixViewStateInputSchema = matrixSchema.pick({ viewState: true });
export type PatchMatrixViewStateInput = z.infer<typeof patchMatrixViewStateInputSchema>;

/**
 * `PATCH /matrices/:id/context` — the matrix-view drawer's Contexte tab
 * (2026-08-26 design doc, D21/D22) editing the two prompt-facing text fields
 * of an already-`généré` matrix.
 *
 * Same derivation as the generic patch above minus `initialPromptOverride`:
 * that field is the récapitulatif's manual prompt override, which only exists
 * while the wizard is still open, so it stays behind the generic route's
 * `brouillon`-only guard rather than becoming editable on a généré matrix
 * through the back door this route deliberately opens for sujet/contexte.
 *
 * `.strict()` and the refine both exist to make the two ways of getting this
 * route wrong loud instead of silent. Plain `.pick()` would *strip* an
 * `initialPromptOverride` sent by a caller assuming this route is a general
 * escape hatch from the brouillon guard, answering 200 while quietly dropping
 * the field; and `{}` would be a 200 that did nothing, when it is far more
 * likely a frontend bug than an intentional request.
 */
export const patchMatrixContextInputSchema = matrixSchema
  .pick({ sujet: true, contexte: true })
  .partial()
  .strict()
  .refine((patch) => patch.sujet !== undefined || patch.contexte !== undefined, {
    message: 'At least one of sujet or contexte must be present.',
  });
export type PatchMatrixContextInput = z.infer<typeof patchMatrixContextInputSchema>;

/** `GET /matrices/:id` — resume-on-reload needs the matrix plus its dimensions. */
export const matrixWithDimensionsSchema = matrixSchema.extend({
  dimensions: z.array(dimensionSchema),
  /**
   * This matrix's own resolved palier table (issue #1) — defaults, then the
   * company's overrides, then the matrix's.
   *
   * The grid used to read `companyWithSetup.paliers` instead, which stopped
   * being right the moment a generation started snapshotting the palier table
   * onto the matrix: renaming a palier for the company would have retitled the
   * columns of every already-generated matrix on screen, while the export —
   * which resolves through the matrix — kept the names the cells were written
   * for. Same source for both, and the two agree again.
   */
  paliers: z.array(resolvedPalierSchema).length(8),
});
export type MatrixWithDimensions = z.infer<typeof matrixWithDimensionsSchema>;

/**
 * `GET /companies/:id/matrices` — the grid's card content (`plan-mss.md` §3):
 * name/sujet (nullable — the frontend picks a placeholder for an untitled
 * draft), dimension count, state, failure flag, timestamps, and which step a
 * `brouillon` draft would resume on. No dimensions/contexte detail — the
 * grid never needs the actual text, and this list can grow large.
 */
export const matrixSummarySchema = matrixSchema
  .pick({
    id: true,
    sujet: true,
    state: true,
    lastGenerationFailedAt: true,
    pivot: true,
    destinataire: true,
    destinataireAutre: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    dimensionCount: z.number().int().min(0),
    /**
     * Which wizard step a `brouillon` draft would resume on, computed
     * server-side from data completeness (no stored `current_step` column —
     * mirrors `deriveInitialStep` in the frontend's `wizard-step.ts`).
     * Meaningless for a `généré` matrix; the grid card only reads it for a
     * `brouillon`/`génération_en_cours` one.
     */
    currentStep: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    /**
     * Who created the matrix, with a short display name (« Alice P. ») for the
     * card footer and the « Créée par » filter (issue #16). Null when the
     * creator is unknown or was deleted.
     */
    createdBy: z
      .object({
        id: z.string().uuid(),
        displayName: z.string(),
      })
      .nullable(),
    /** The folder the matrix sits in (issue #19); null = « Hors dossier ». */
    folderId: z.string().uuid().nullable(),
    /**
     * Open discussions on the matrix, its cells, rows and columns **that the
     * caller may see** (issue #19): a private note of someone else, or a PMS
     * team note for a client role, never counts.
     */
    openNoteCount: z.number().int().min(0),
    /** The first characters of the memo, null while it is empty. */
    memoPreview: z.string().max(MEMO_PREVIEW_MAX).nullable(),
    /**
     * The latest open note or reply the caller may see, for the card tooltip;
     * null when there is none. `authorName` is null once the author is gone.
     */
    lastNote: z
      .object({
        authorName: z.string().nullable(),
        createdAt: z.string().datetime(),
        excerpt: z.string().max(NOTE_EXCERPT_MAX),
      })
      .nullable(),
  });
export type MatrixSummary = z.infer<typeof matrixSummarySchema>;

/** `POST /matrices/:id/duplicate` (201) — the full copy, as it appears in the grid (issue #16). */
export const duplicateMatrixResponseSchema = matrixSummarySchema;
export type DuplicateMatrixResponse = z.infer<typeof duplicateMatrixResponseSchema>;

/**
 * `POST /matrices/:id/derive` (201) — a new draft pre-filled from the source
 * (issue #16). `sourceSujet` feeds the wizard banner; `pivotFellBack` is true
 * when the source pivot was blank for a step and the draft fell back to
 * `levier_valeur`.
 */
export const deriveMatrixResponseSchema = z.object({
  matrix: matrixSchema,
  sourceSujet: z.string().nullable(),
  pivotFellBack: z.boolean(),
});
export type DeriveMatrixResponse = z.infer<typeof deriveMatrixResponseSchema>;
